import { DateTime } from "luxon";
import { nextTick, watch } from "vue";
import type { LocationQuery } from "vue-router";
import { useRoute, useRouter } from "vue-router";

import { useGlobalFiltersStore } from "@/shared/stores/useGlobalFiltersStore";
import { useVendorFiltersStore } from "@/shared/stores/useVendorFiltersStore";

/**
 * Parse comma-separated string to array
 */
function parseArrayParam(param?: string | string[]): string[] {
  const value = Array.isArray(param) ? param[0] : param;
  if (!value) return [];
  return value.split(",").filter(Boolean);
}

/**
 * Parse ISO date strings to Date objects
 * Returns null if invalid or missing
 */
function parseDateRange(since?: string | string[], until?: string | string[]): [Date, Date] | null {
  // Handle array values from LocationQuery (take first value)
  const sinceStr = Array.isArray(since) ? since[0] : since;
  const untilStr = Array.isArray(until) ? until[0] : until;

  if (!sinceStr || !untilStr) return null;

  try {
    const sinceDate = DateTime.fromISO(sinceStr, { zone: "utc" });
    const untilDate = DateTime.fromISO(untilStr, { zone: "utc" });

    if (sinceDate.isValid && untilDate.isValid) {
      return [sinceDate.toJSDate(), untilDate.toJSDate()];
    }
  }
  catch (error) {
    console.error("Failed to parse date range:", error);
  }

  return null;
}

/**
 * Query keys owned by the global filters: every key `syncFromUrl` reads and every
 * key the Store → URL functions below write. Anything else in the query belongs to
 * the page (e.g. `lag`) and must not re-apply the filters when it changes.
 */
const FILTER_QUERY_KEYS = [
  "primary_since",
  "primary_until",
  "secondary_since",
  "secondary_until",
  "brands",
  "channels",
  "vendor_brands",
  "products",
  "vendor_products",
  "granularity",
  "activeOnly",
] as const;

/**
 * Check if two location queries are equal for the keys the filters own
 * Used to prevent unnecessary store updates when only hash or a page-owned key changes
 */
function isQueryEqual(query1: LocationQuery, query2: LocationQuery | undefined): boolean {
  if (!query2) return false;
  if (query1 === query2) return true;

  for (const key of FILTER_QUERY_KEYS) {
    const val1 = query1[key];
    const val2 = query2[key];

    // Both are arrays
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      // Assume order matters for simplicity (router usually preserves order)
      if (val1.some((v, i) => v !== val2[i])) return false;
    }
    // Type mismatch or value mismatch
    else if (val1 !== val2) {
      return false;
    }
  }

  return true;
}

/**
 * Composable for syncing global filters store with URL query params
 *
 * Provides explicit functions to update URL instead of automatic watchers
 * This gives precise control - only changed params are updated in URL
 *
 * Usage:
 * const { updatePrimaryInterval, updateSecondaryInterval } = useGlobalFiltersSync();
 *
 * // When user changes primary date picker:
 * store.setPrimaryIntervalRange(newRange);
 * updatePrimaryInterval();
 *
 * // When user changes secondary date picker:
 * store.setSecondaryIntervalRange(newRange);
 * updateSecondaryInterval();
 */

// Global singleton flag to prevent circular URL updates
// Shared between all useGlobalFiltersSync() instances
let isUpdatingUrl = false;

// Track if watch is already registered (prevent multiple watches)
let isWatchRegistered = false;

export function useGlobalFiltersSync() {
  const store = useGlobalFiltersStore();
  const vendorStore = useVendorFiltersStore();
  const route = useRoute();
  const router = useRouter();

  /**
     * Helper to update URL without triggering the watch
     */
  async function updateUrlSilently(updateFn: () => Promise<void>) {
    isUpdatingUrl = true;
    try {
      await updateFn();
      // `router.replace` resolving is not the end of it: `route.query` is
      // reactive and the URL → Store watcher below runs on the pre-flush
      // queue, i.e. after this microtask chain unwinds. Releasing the flag in
      // a plain `finally` therefore un-guards it before it ever fires, and our
      // own write gets echoed back into the store as if it were browser
      // navigation — re-applying URL values over local state that has legitimately
      // moved on. Waiting a tick makes the guard actually cover the watcher.
      await nextTick();
    }
    finally {
      isUpdatingUrl = false;
    }
  }

  // ==================== URL → Store (Init & Browser Navigation) ====================

  /**
     * Update store from URL query params
     * Called on init and browser navigation (back/forward)
     * Uses withoutAbort to prevent cancelling requests during URL sync
     */
  function syncFromUrl(query: LocationQuery) {
    store.withoutAbort(() => {
      // Primary interval (always present)
      // Pass false for autoUpdateSecondary to prevent overwriting secondary from URL
      const primaryRange = parseDateRange(query.primary_since, query.primary_until);
      if (primaryRange) {
        store.setPrimaryIntervalRange(primaryRange, false);
      }

      // Secondary interval (only if params exist)
      const secondaryRange = parseDateRange(query.secondary_since, query.secondary_until);
      if (secondaryRange) {
        store.setSecondaryIntervalRange(secondaryRange);
      }

      // Brands filter (by shortName)
      const brandShortNames = parseArrayParam(query.brands);
      store.setBrandsFromShortNames(brandShortNames);

      // Channels filter
      const channelIds = parseArrayParam(query.channels).map(Number);
      store.setChannelsFromIds(channelIds);

      // Vendor brands filter (by shortName) — only after the vendor list is
      // loaded, otherwise restore would wipe the selection
      if (vendorStore.isInitialized) {
        const vendorBrandShortNames = parseArrayParam(query.vendor_brands);
        vendorStore.setVendorBrandsFromShortNames(vendorBrandShortNames);
      }

      // Note: Products (seller and vendor) are NOT synced here to avoid
      // multiple requests. Products are loaded once in init

      // Granularity filter
      const granularityParam = query.granularity as string | undefined;
      if (granularityParam && ["MONTH", "WEEK", "DAY"].includes(granularityParam)) {
        store.setGranularity(granularityParam as "MONTH" | "WEEK" | "DAY");
      }

      // Active-only filter (default true - only restore explicit "false")
      if (query.activeOnly === "false") {
        void store.setActiveOnly(false);
      }

      // Future filters:
      // if (query.project_id) store.setProjectId(query.project_id);
      // if (query.status) store.setStatus(query.status);
    });
  }

  // ==================== Store → URL (Explicit Updates) ====================

  /**
     * Update only primary interval in URL
     * Call this after user changes primary date picker
     */
  async function updatePrimaryInterval() {
    await updateUrlSilently(async () => {
      await router.replace({
        query: {
          ...route.query,
          primary_since: store.formattedPrimaryInterval.since,
          primary_until: store.formattedPrimaryInterval.until,
        },
      });
    });
  }

  /**
     * Update only secondary interval in URL
     * Call this after user changes secondary date picker
     */
  async function updateSecondaryInterval() {
    await updateUrlSilently(async () => {
      await router.replace({
        query: {
          ...route.query,
          secondary_since: store.formattedSecondaryInterval.since,
          secondary_until: store.formattedSecondaryInterval.until,
        },
      });
    });
  }

  /**
     * Update primary and secondary intervals in URL
     * Needed when changing primary interval affects secondary interval
     */
  async function updateBothIntervals() {
    await updateUrlSilently(async () => {
      await router.replace({
        query: {
          ...route.query,
          primary_since: store.formattedPrimaryInterval.since,
          primary_until: store.formattedPrimaryInterval.until,
          secondary_since: store.formattedSecondaryInterval.since,
          secondary_until: store.formattedSecondaryInterval.until,
        },
      });
    });
  }

  /**
     * Update brands in URL
     * Call this after user changes brands filter
     * Uses shortName for URL params
     */
  async function updateBrands() {
    await updateUrlSilently(async () => {
      const shortNames = store.selectedBrandIds; // Already returns shortNames
      if (shortNames.length === 0) {
        // "All" selected - remove from URL
        const restQuery = { ...route.query };
        delete restQuery.brands;
        await router.replace({ query: restQuery });
      }
      else {
        await router.replace({
          query: {
            ...route.query,
            brands: shortNames.join(","),
          },
        });
      }
    });
  }

  /**
     * Update vendor brands in URL
     * Call this after user changes the vendor brands filter
     * Uses shortName for URL params
     */
  async function updateVendorBrands() {
    await updateUrlSilently(async () => {
      const shortNames = vendorStore.selectedVendorBrandIds; // Already returns shortNames
      if (shortNames.length === 0) {
        // "All" selected - remove from URL
        const restQuery = { ...route.query };
        delete restQuery.vendor_brands;
        await router.replace({ query: restQuery });
      }
      else {
        await router.replace({
          query: {
            ...route.query,
            vendor_brands: shortNames.join(","),
          },
        });
      }
    });
  }

  /**
     * Update channels in URL
     * Call this after user changes channels filter
     */
  async function updateChannels() {
    await updateUrlSilently(async () => {
      const ids = store.selectedChannelIds;
      if (ids.length === 0) {
        // "All" selected - remove from URL
        const restQuery = { ...route.query };
        delete restQuery.channels;
        await router.replace({ query: restQuery });
      }
      else {
        await router.replace({
          query: {
            ...route.query,
            channels: ids.join(","),
          },
        });
      }
    });
  }

  /**
     * Update products in URL
     * Call this after user changes product search filter
     */
  async function updateProducts() {
    await updateUrlSilently(async () => {
      const products = store.productSearch;
      if (products.length === 0) {
        // No products - remove from URL
        const restQuery = { ...route.query };
        delete restQuery.products;
        await router.replace({ query: restQuery });
      }
      else {
        await router.replace({
          query: {
            ...route.query,
            products: products.join(","),
          },
        });
      }
    });
  }

  /**
     * Update vendor products in URL
     * Call this after user changes the vendor product search filter
     */
  async function updateVendorProducts() {
    await updateUrlSilently(async () => {
      const products = vendorStore.vendorProductSearch;
      if (products.length === 0) {
        // No products - remove from URL
        const restQuery = { ...route.query };
        delete restQuery.vendor_products;
        await router.replace({ query: restQuery });
      }
      else {
        await router.replace({
          query: {
            ...route.query,
            vendor_products: products.join(","),
          },
        });
      }
    });
  }

  /**
     * Update granularity in URL
     * Call this after user changes granularity filter
     */
  async function updateGranularity() {
    await updateUrlSilently(async () => {
      const value = store.granularity;
      if (value === "MONTH") {
        // Default value - remove from URL
        const restQuery = { ...route.query };
        delete restQuery.granularity;
        await router.replace({ query: restQuery });
      }
      else {
        await router.replace({
          query: {
            ...route.query,
            granularity: value,
          },
        });
      }
    });
  }

  /**
     * Update activeOnly in URL
     * Call this after user toggles the active-only filter
     */
  async function updateActiveOnly() {
    await updateUrlSilently(async () => {
      const value = store.activeOnly;
      if (value) {
        // Default value - remove from URL
        const { activeOnly: _activeOnly, ...restQuery } = route.query;
        await router.replace({ query: restQuery });
      }
      else {
        await router.replace({
          query: {
            ...route.query,
            activeOnly: "false",
          },
        });
      }
    });
  }

  const resetAllFilters = async () => {
    await updateUrlSilently(async () => {
      await router.replace({
        query: {},
      });
    });
  };

  // ==================== Watchers (Only for Browser Navigation) ====================

  /**
     * Sync URL → Store (browser back/forward)
     * This is the ONLY watcher - no automatic Store → URL sync
     * Skips if store is not initialized yet (handled by initFilters in MasterLayout)
     * Skips if we're updating URL ourselves (to prevent circular updates)
     * Only registered ONCE (first call to useGlobalFiltersSync)
     */
  if (!isWatchRegistered) {
    isWatchRegistered = true;
    watch(
      () => route.query,
      (newQuery, oldQuery) => {
        // Skip if we're updating URL ourselves
        if (isUpdatingUrl) return;

        // Skip if store not initialized - initFilters will handle initial sync
        if (!store.isInitialized) return;

        // Skip if query didn't change (e.g. hash change or re-navigation with same params)
        if (isQueryEqual(newQuery, oldQuery)) return;

        syncFromUrl(newQuery);
      },
    );
  }

  return {
    updatePrimaryInterval,
    updateSecondaryInterval,
    updateBothIntervals,
    updateBrands,
    updateVendorBrands,
    updateChannels,
    updateProducts,
    updateVendorProducts,
    updateGranularity,
    updateActiveOnly,
    resetAllFilters,
  };
}
